// The item panel's ledger stock for local items (materials, sub-assemblies): the balance at each
// location, recent moves, and a count or adjustment (each one a permanent, audited stock move).
// Shopify keeps sell items' stock; StockList in ItemsPage shows those levels.
import { useMemo, useState } from "react";
import { numberOf } from "../../../shared/calc.ts";
import type { Item, StockMove } from "../../../shared/db.gen.ts";
import { keepsLedger, STOCK_REASON_LABELS, type StockReason } from "../../../shared/receiving.ts";
import { ago, money, qty } from "../lib/format.ts";
import { can, mutateWithResult } from "../lib/mutate.ts";
import { useRows } from "../lib/sync.ts";
import { Button, cx, Input, Select } from "../ui/controls.tsx";
import { type Column, DataTable } from "../ui/DataTable.tsx";
import { Section } from "../ui/RecordPanel.tsx";
import { PaneLink } from "../ui/SidePane.tsx";
import { toast } from "../ui/toast.tsx";

export function LocalStock({ item }: { item: Item }) {
  const balances = useRows("stock_balance");
  const moves = useRows("stock_move");
  const locations = useRows("location");
  const receipts = useRows("receipt");
  const users = useRows("app_user");
  const [form, setForm] = useState<"count" | "adjust" | null>(null);

  const mine = useMemo(() => balances.filter((b) => b.item_id === item.id), [balances, item.id]);
  const history = useMemo(() => moves.filter((m) => m.item_id === item.id), [moves, item.id]);
  const name = (id: string | null) => locations.find((l) => l.id === id)?.name ?? "";
  const userName = (id: string | null) => (id ? (users.find((u) => u.id === id)?.name ?? "") : "");
  const receiptOf = (m: StockMove) => (m.ref_type === "receipt" ? receipts.find((r) => r.id === m.ref_id) : undefined);
  const inward = (m: StockMove) => m.to_location_id !== null;
  const reason = (m: StockMove) => STOCK_REASON_LABELS[m.reason as StockReason] ?? m.reason;
  const detail = (m: StockMove) => (m.reason === "count" ? `counted ${qty(m.counted)}${m.notes ? ` · ${m.notes}` : ""}` : m.notes);
  const moveColumns: Column<StockMove>[] = [
    {
      key: "at",
      header: "When",
      width: "96px",
      text: (m) => m.at,
      cell: (m) => (
        <span className="text-faint" title={new Date(m.at).toLocaleString()}>
          {ago(m.at)}
        </span>
      ),
    },
    { key: "reason", header: "Reason", width: "110px", value: reason, cell: (m) => <span className="truncate">{reason(m)}</span> },
    {
      key: "detail",
      header: "Detail",
      headerTitle: "Its receipt, the count, or its note",
      width: "minmax(120px,1.5fr)",
      value: (m) => receiptOf(m)?.number ?? detail(m),
      cell: (m) => {
        const receipt = receiptOf(m);
        return (
          <span className="truncate text-muted" title={m.notes || undefined}>
            {receipt ? (
              <PaneLink kind="po" id={receipt.po_id} to={`/pos?open=${receipt.po_id}`} className="hover:underline">
                {receipt.number}
              </PaneLink>
            ) : (
              detail(m)
            )}
          </span>
        );
      },
    },
    {
      key: "location",
      header: "Location",
      width: "minmax(90px,1fr)",
      // Shown before only with stock in two places or a move between them: a column most panels don't need
      optional: mine.length <= 1 && !history.some((m) => m.from_location_id),
      value: (m) => name(m.to_location_id ?? m.from_location_id),
      cell: (m) => <span className="truncate text-muted">{name(m.to_location_id ?? m.from_location_id)}</span>,
    },
    {
      key: "by",
      header: "By",
      width: "minmax(80px,1fr)",
      value: (m) => userName(m.by),
      cell: (m) => <span className="truncate text-muted">{userName(m.by)}</span>,
    },
    {
      key: "qty",
      header: "Qty",
      width: "72px",
      align: "right",
      value: (m) => (inward(m) ? 1 : -1) * Number(m.qty),
      cell: (m) => (
        <span className={cx("font-mono", inward(m) ? "text-ok" : "text-danger")}>{Number(m.qty) === 0 ? "±0" : `${inward(m) ? "+" : "−"}${qty(m.qty)}`}</span>
      ),
    },
  ];
  if (!keepsLedger(item)) return null;

  const total = mine.reduce((n, b) => n + Number(b.qty), 0);
  const editable = can("stock.adjust");

  return (
    <Section title="Stock" count={mine.length || undefined}>
      <div className="px-2 pb-2">
        {!mine.length && <div className="px-2 pb-1 text-muted">None recorded yet. Receipts add stock; a count sets it.</div>}
        {mine.map((b) => (
          <div key={b.id} className="flex items-baseline justify-between gap-2 rounded-md px-2 py-1 hover:bg-hover">
            <span className="truncate">{name(b.location_id) || "Unknown location"}</span>
            <span className="flex items-baseline gap-2">
              <span className="text-[11px] text-faint">{b.last_counted_at ? `counted ${ago(b.last_counted_at)}` : "never counted"}</span>
              <span className={cx("font-mono text-[12px]", Number(b.qty) < 0 && "text-danger")}>
                {qty(b.qty)} {item.uom}
              </span>
            </span>
          </div>
        ))}
        {mine.length > 1 && (
          <div className="flex justify-between px-2 pt-0.5 text-[12px] text-muted">
            <span>Total</span>
            <span className="font-mono">
              {qty(total)} {item.uom}
            </span>
          </div>
        )}
        {item.std_cost != null && total > 0 && <div className="px-2 text-right text-[11px] text-faint">worth {money(total * Number(item.std_cost))}</div>}

        {editable && !form && (
          <div className="mt-1 flex gap-1 px-1">
            <Button size="sm" variant="ghost" onClick={() => setForm("count")}>
              Count
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setForm("adjust")}>
              Adjust
            </Button>
          </div>
        )}
        {form && <AdjustForm item={item} kind={form} current={mine} onDone={() => setForm(null)} />}

        {history.length > 0 && (
          <div className="mt-2">
            <div className="flex items-center gap-2 px-2 pb-1">
              <span className="text-[11px] text-faint">Moves</span>
            </div>
            <DataTable
              fit
              rows={history}
              columns={moveColumns}
              defaultSort={{ key: "at", desc: true }}
              rowNoun={["move", "moves"]}
              exportName="Stock moves"
              pageFilters={false}
            />
          </div>
        )}
      </div>
    </Section>
  );
}

function AdjustForm({
  item,
  kind,
  current,
  onDone,
}: {
  item: Item;
  kind: "count" | "adjust";
  current: { location_id: string; qty: number }[];
  onDone: () => void;
}) {
  const locations = useRows("location");
  const usable = locations.filter((l) => l.active && !l.ignored);
  const [locationId, setLocationId] = useState(current[0]?.location_id ?? usable.find((l) => l.kind === "hhl")?.id ?? usable[0]?.id ?? "");
  const [value, setValue] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [id] = useState(() => crypto.randomUUID()); // one move, however many times Save is pressed

  const have = Number(current.find((c) => c.location_id === locationId)?.qty ?? 0);
  const n = value.trim() === "" ? null : numberOf(value);
  const valid = n !== null && Number.isFinite(n) && (kind === "count" ? n >= 0 : n !== 0);
  const change = valid ? (kind === "count" ? (n as number) - have : (n as number)) : 0;

  const save = async () => {
    if (!valid || saving) return;
    if (kind === "adjust" && !notes.trim()) return toast.error("Say why the stock is changing");
    setSaving(true);
    const { ok } = await mutateWithResult("stock.adjust", { id, item_id: item.id, location_id: locationId, kind, qty: n as number, notes: notes.trim() });
    setSaving(false);
    if (ok) {
      toast.ok(kind === "count" ? "Count saved" : "Stock adjusted");
      onDone();
    }
  };

  return (
    <form
      className="mt-1 space-y-1.5 rounded-md border border-line bg-surface-2/60 p-2"
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
    >
      <div className="flex gap-1.5">
        <Select
          value={locationId}
          onChange={(e) => setLocationId(e.target.value)}
          options={usable.map((l) => ({ value: l.id, label: l.name }))}
          className="h-8 flex-1"
        />
        <div className="w-28 shrink-0">
          <Input
            autoFocus
            inputMode="decimal"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={kind === "count" ? "Counted" : "+10 or −5"}
            className="h-8 text-right font-mono"
            aria-label={kind === "count" ? "Counted quantity" : "Change"}
          />
        </div>
      </div>
      <Input
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder={kind === "count" ? "Note (optional)" : "Why? (required)"}
        className="h-8"
        aria-label="Note"
      />
      <div className="flex items-center gap-2">
        <span className="flex-1 text-[12px] text-muted">
          {valid ? `${qty(have)} → ${qty(have + change)} ${item.uom} (${change >= 0 ? "+" : "−"}${qty(Math.abs(change))})` : `Now ${qty(have)} ${item.uom}`}
        </span>
        <Button size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button size="sm" variant="primary" type="submit" disabled={!valid || saving}>
          {kind === "count" ? "Save count" : "Adjust"}
        </Button>
      </div>
    </form>
  );
}
